import type { ReactNode } from 'react';
import { Activity, Banknote, Bot, CircleDollarSign, Coins, FlaskConical, Gauge, Hourglass, Leaf, Map, MessagesSquare, ShieldCheck, Sprout, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { Card } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { useUso } from '../uso-api';
import { useAutoaprendizado } from '../autoaprendizado-api';
import { useQualidade } from '../qualidade-api';
import type { Uso, UsoAgente } from '../uso-schemas';
import { AgentesModuleTabs } from './AgentesModuleTabs';

const CARD = 'rounded-[14px] border border-border bg-elevated dark:border-transparent';
type Tone = 'ok' | 'warning' | 'danger' | 'info' | 'accent' | 'muted';
const TONE: Record<Tone, string> = { ok: 'var(--ok)', warning: 'var(--warning)', danger: 'var(--danger)', info: 'var(--info)', accent: 'var(--accent)', muted: 'var(--text-muted)' };

const fmtInt = (v: number) => Math.round(v).toLocaleString('pt-BR');
function fmtBRL(v: number): string {
  if (v >= 1000) return `R$ ${(v / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtTok(v: number): string {
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} mil`;
  return String(v);
}
const DOM_ICON: Record<string, LucideIcon> = { credito: Banknote, ambiental: Leaf, agronomia: Sprout, fundiario: Map, infra: Bot };
function ativo(ultima: string | null): boolean {
  if (!ultima) return false;
  return Date.now() - new Date(ultima).getTime() < 7 * 24 * 3600 * 1000;
}

function Shell({ active, title, children }: { active: 'visao-geral' | 'custo' | 'qualidade'; title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-bg">
      <AgentesModuleTabs active={active} className="px-8 pt-3" />
      <div className="px-8 pb-4 pt-5"><h1 className="text-[18px] font-semibold leading-6 text-text">{title}</h1></div>
      <div className="flex-1 px-8 pb-8">{children}</div>
    </div>
  );
}
function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-muted">{children}</p>;
}
function Kpi({ icon: Icon, label, value, unit, foot, footTone }: { icon: LucideIcon; label: string; value: string; unit?: string; foot?: string; footTone?: Tone }) {
  return (
    <div className={cn('flex flex-1 basis-[180px] flex-col gap-2.5 p-4', CARD)}>
      <span className="flex size-8 items-center justify-center rounded-[8px] bg-[var(--accent)]"><Icon size={16} strokeWidth={1.9} className="text-[var(--on-solid)]" /></span>
      <span className="text-[10.5px] font-medium uppercase tracking-[0.04em] text-text-muted">{label}</span>
      <div className="flex items-baseline gap-1">
        <span className="text-[22px] font-bold tracking-tight text-text">{value}</span>
        {unit && <span className="text-xs font-medium text-text-muted">{unit}</span>}
      </div>
      {foot && <span className="flex items-center gap-1.5 text-[11px] text-text-muted">{footTone && <span className="inline-block size-[6px] rounded-full" style={{ background: TONE[footTone] }} />}{foot}</span>}
    </div>
  );
}

function agentesChat(u: Uso): UsoAgente[] {
  return u.agentes.filter((a) => a.dominio !== 'infra' && a.dominio !== 'outro');
}

export function VisaoGeralView() {
  const uso = useUso();
  const ap = useAutoaprendizado();
  if (uso.isLoading) return <Shell active="visao-geral" title="Visão geral"><Card padding="lg"><p className="text-sm text-text-muted">Carregando…</p></Card></Shell>;
  if (uso.error || !uso.data) {
    const msg = (uso.error as { status?: number })?.status === 403 ? 'Acesso restrito a administradores.' : 'Falha ao carregar.';
    return <Shell active="visao-geral" title="Visão geral"><Card padding="lg"><p className="text-sm text-danger">{msg}</p></Card></Shell>;
  }
  const u = uso.data;
  const apr = ap.data?.resumo;
  return (
    <Shell active="visao-geral" title="Visão geral">
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-3.5">
          <Kpi icon={MessagesSquare} label="Conversas · 7 dias" value={fmtInt(u.totais.conversas_7d)} foot={`${fmtInt(u.totais.conversas_30d)} em 30 dias`} footTone="info" />
          <Kpi icon={CircleDollarSign} label="Custo · 30 dias" value={fmtBRL(u.totais.custo_30d)} foot="estimado" footTone="muted" />
          <Kpi icon={Hourglass} label="Regras em quarentena" value={apr ? fmtInt(apr.em_quarentena ?? 0) : '—'} foot="autoaprendizado" footTone="warning" />
          <Kpi icon={ShieldCheck} label="Quebras ativas" value={apr ? fmtInt(apr.quebras_ativas ?? 0) : '—'} foot={apr?.quebras_ativas ? 'atenção' : 'nenhuma'} footTone={apr?.quebras_ativas ? 'danger' : 'ok'} />
        </div>
        <SectionLabel>Agentes</SectionLabel>
        <div className="flex flex-wrap gap-4">
          {agentesChat(u).map((a) => {
            const Icon = DOM_ICON[a.dominio] ?? Bot;
            const on = ativo(a.ultima);
            return (
              <div key={a.agent_id} className={cn('flex flex-1 basis-[240px] flex-col gap-3.5 p-[18px]', CARD)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-[38px] items-center justify-center rounded-[10px] bg-[var(--accent)]"><Icon size={19} strokeWidth={1.9} className="text-[var(--on-solid)]" /></span>
                    <div className="flex flex-col">
                      <span className="text-[15px] font-semibold text-text">{a.nome}</span>
                      <span className="text-[11.5px] capitalize text-text-muted">{a.dominio}</span>
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold" style={{ color: TONE[on ? 'ok' : 'muted'], background: `color-mix(in srgb, ${TONE[on ? 'ok' : 'muted']} 14%, transparent)` }}>
                    <span className="size-[6px] rounded-full" style={{ background: TONE[on ? 'ok' : 'muted'] }} />{on ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="flex items-stretch justify-between border-t border-border/70 pt-3.5 dark:border-white/5">
                  {[[fmtInt(a.conversas_7d), 'conversas 7d'], [fmtTok(a.tokens_30d), 'tokens 30d'], [fmtBRL(a.custo_30d), 'custo 30d']].map(([v, l]) => (
                    <div key={l} className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[16px] font-bold text-text">{v}</span>
                      <span className="text-[10.5px] text-text-muted">{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );
}

export function CustoView() {
  const uso = useUso();
  if (uso.isLoading) return <Shell active="custo" title="Custo & uso"><Card padding="lg"><p className="text-sm text-text-muted">Carregando…</p></Card></Shell>;
  if (uso.error || !uso.data) {
    const msg = (uso.error as { status?: number })?.status === 403 ? 'Acesso restrito a administradores.' : 'Falha ao carregar.';
    return <Shell active="custo" title="Custo & uso"><Card padding="lg"><p className="text-sm text-danger">{msg}</p></Card></Shell>;
  }
  const u = uso.data;
  const maxCusto = Math.max(...u.agentes.map((a) => a.custo_30d), 0.01);
  const custoConv = u.totais.conversas_30d > 0 ? u.totais.custo_30d / u.totais.conversas_30d : 0;
  const MODEL_TONE: Tone[] = ['accent', 'warning', 'info', 'muted'];
  return (
    <Shell active="custo" title="Custo & uso">
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-3.5">
          <Kpi icon={CircleDollarSign} label="Custo · 30 dias" value={fmtBRL(u.totais.custo_30d)} foot="estimado" footTone="muted" />
          <Kpi icon={Coins} label="Tokens · 30 dias" value={fmtTok(u.totais.tokens_30d)} foot="entrada + saída" footTone="info" />
          <Kpi icon={MessagesSquare} label="Conversas · 30 dias" value={fmtInt(u.totais.conversas_30d)} foot="frota inteira" footTone="ok" />
          <Kpi icon={Activity} label="Custo por conversa" value={fmtBRL(custoConv)} foot="média · estimado" footTone="muted" />
        </div>
        <div className="flex flex-wrap items-start gap-4">
          <div className={cn('flex flex-1 basis-[420px] flex-col gap-4 p-[22px]', CARD)}>
            <SectionLabel>Custo por agente · 30 dias</SectionLabel>
            <div className="flex flex-col gap-3">
              {u.agentes.map((a) => (
                <div key={a.agent_id} className="flex items-center gap-3">
                  <span className="w-24 shrink-0 truncate text-[12.5px] font-medium text-text">{a.nome}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: 'rgba(140,135,128,0.18)' }}>
                    <span className="block h-full rounded-full bg-[var(--accent)]" style={{ width: `${(a.custo_30d / maxCusto) * 100}%` }} />
                  </span>
                  <span className="w-[74px] text-right text-[12.5px] font-semibold text-text">{fmtBRL(a.custo_30d)}</span>
                  <span className="w-[64px] text-right text-[11.5px] text-text-muted">{fmtInt(a.conversas_30d)} conv.</span>
                </div>
              ))}
            </div>
          </div>
          <div className={cn('flex flex-1 basis-[320px] flex-col gap-4 p-[22px]', CARD)}>
            <SectionLabel>Custo por modelo</SectionLabel>
            <div className="flex h-4 w-full gap-[3px] overflow-hidden rounded-full">
              {u.modelos.filter((m) => m.custo > 0).map((m, i) => (
                <div key={m.model} className="h-full" style={{ flexGrow: Math.max(Math.round(m.custo * 100), 1), background: TONE[MODEL_TONE[i] ?? 'muted'] }} />
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {u.modelos.map((m, i) => (
                <div key={m.model} className="flex items-center gap-2.5">
                  <span className="size-[9px] rounded-full" style={{ background: TONE[MODEL_TONE[i] ?? 'muted'] }} />
                  <span className="flex-1 truncate text-[12.5px] font-medium text-text">{m.model}</span>
                  <span className="text-[12px] text-text-muted">{fmtTok(m.tokens)}</span>
                  <span className="text-[12.5px] font-semibold text-text">{m.pct_custo}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-text-muted">Custo é estimativa (preço por modelo configurado no backend); tokens e volume são reais (conversa_agente, 30 dias).</p>
      </div>
    </Shell>
  );
}

function scoreTone(s: number | null): Tone {
  if (s == null) return 'muted';
  if (s >= 0.8) return 'ok';
  if (s >= 0.6) return 'warning';
  return 'danger';
}
const fmtScore = (s: number | null) => (s == null ? '—' : s.toFixed(2).replace('.', ','));
const fmtPct1 = (s: number | null) => (s == null ? '—' : `${(s * 100).toFixed(1).replace('.', ',')}%`);

export function QualidadeView() {
  const q = useQualidade();
  if (q.isLoading) return <Shell active="qualidade" title="Qualidade & evals"><Card padding="lg"><p className="text-sm text-text-muted">Carregando…</p></Card></Shell>;
  if (q.error || !q.data) {
    const msg = (q.error as { status?: number })?.status === 403 ? 'Acesso restrito a administradores.' : 'Falha ao carregar.';
    return <Shell active="qualidade" title="Qualidade & evals"><Card padding="lg"><p className="text-sm text-danger">{msg}</p></Card></Shell>;
  }
  const d = q.data;
  if (!d.fonte_ok) {
    return (
      <Shell active="qualidade" title="Qualidade & evals">
        <div className={cn('flex flex-col items-center gap-2 p-10 text-center', CARD)}>
          <span className="flex size-11 items-center justify-center rounded-full" style={{ background: 'color-mix(in srgb, var(--warning) 14%, transparent)' }}><FlaskConical size={20} className="text-[var(--warning)]" /></span>
          <p className="text-sm font-medium text-text">Fonte de qualidade (Phoenix) indisponível</p>
          <p className="max-w-sm text-xs text-text-muted">Os scores de eval vêm do Phoenix. Assim que a fonte responder, os dados aparecem aqui.</p>
        </div>
      </Shell>
    );
  }
  const r = d.resumo;
  return (
    <Shell active="qualidade" title="Qualidade & evals">
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-3.5">
          <Kpi icon={Gauge} label="Qualidade média · frota" value={fmtScore(r.quality_media)} foot={`${fmtInt(r.quality_n)} avaliações`} footTone="info" />
          <Kpi icon={ShieldCheck} label="Evaluators ativos" value={fmtInt(r.evaluators_ativos)} foot="automáticos" footTone="ok" />
          <Kpi icon={FlaskConical} label="Goldsets" value={fmtInt(r.goldsets)} foot="retrieval" footTone="muted" />
        </div>

        <div className={cn('flex flex-col gap-4 p-[22px]', CARD)}>
          <SectionLabel>Qualidade por agente · judge (30 dias)</SectionLabel>
          <div className="flex flex-col gap-3.5">
            {d.agentes.map((a) => {
              const tone = scoreTone(a.score);
              return (
                <div key={a.projeto} className="flex items-center gap-3">
                  <div className="flex w-28 shrink-0 flex-col">
                    <span className="text-[13px] font-medium text-text">{a.nome}</span>
                    <span className="text-[10.5px] capitalize text-text-muted">{a.dominio}</span>
                  </div>
                  <span className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: 'rgba(140,135,128,0.18)' }}>
                    <span className="block h-full rounded-full" style={{ width: `${(a.score ?? 0) * 100}%`, background: TONE[tone] }} />
                  </span>
                  <span className="w-12 text-right text-[13px] font-semibold tabular-nums" style={{ color: TONE[tone] }}>{fmtScore(a.score)}</span>
                  <span className="w-16 text-right text-[11px] text-text-muted">{fmtInt(a.n)} aval.</span>
                  <span className="flex w-16 items-center justify-end gap-1 text-[11px] font-medium tabular-nums" style={{ color: a.delta == null ? 'var(--text-muted)' : a.delta >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
                    {a.delta == null ? '—' : (<>{a.delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{a.delta >= 0 ? '+' : ''}{fmtScore(a.delta)}</>)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-text-muted">Score 0–1 do LLM-judge (relevância, completude, clareza, segurança). Δ vs. os 30 dias anteriores.</p>
        </div>

        <div className="flex flex-wrap items-start gap-4">
          <div className={cn('flex flex-1 basis-[360px] flex-col gap-4 p-[22px]', CARD)}>
            <SectionLabel>Evaluators automáticos</SectionLabel>
            <div className="flex flex-col gap-3">
              {d.evaluators.map((e) => {
                const tone = scoreTone(e.score);
                const tipoColor = e.tipo === 'LLM' ? 'var(--info)' : 'var(--text-muted)';
                return (
                  <div key={`${e.projeto}:${e.nome}`} className="flex items-center gap-2.5">
                    <span className="rounded-[5px] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide" style={{ color: tipoColor, background: `color-mix(in srgb, ${tipoColor} 12%, transparent)` }}>{e.tipo}</span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[12px] font-medium text-text">{e.nome}</span>
                      <span className="text-[10.5px] text-text-muted">{e.agente} · {fmtInt(e.n)} aval.</span>
                    </div>
                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: TONE[tone] }}>{fmtScore(e.score)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={cn('flex flex-1 basis-[360px] flex-col gap-4 p-[22px]', CARD)}>
            <SectionLabel>Recuperação · goldset</SectionLabel>
            {d.retrieval.length === 0 ? (
              <p className="text-xs text-text-muted">Nenhum goldset com métricas ainda.</p>
            ) : (
              d.retrieval.map((g) => (
                <div key={g.dataset} className="flex flex-col gap-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13px] font-semibold text-text">{g.nome}</span>
                    <span className="text-[10.5px] text-text-muted">{g.experiment}{g.data ? ` · ${g.data.slice(0, 10)}` : ''}</span>
                  </div>
                  <div className="flex gap-2">
                    {([['recall@5', fmtPct1(g.recall_5)], ['recall@20', fmtPct1(g.recall_20)], ['MRR', fmtScore(g.mrr)]] as const).map(([l, v]) => (
                      <div key={l} className="flex flex-1 flex-col gap-0.5 rounded-[10px] p-2.5" style={{ background: 'color-mix(in srgb, var(--text-muted) 7%, transparent)' }}>
                        <span className="text-[16px] font-bold tabular-nums text-text">{v}</span>
                        <span className="text-[10px] text-text-muted">{l}</span>
                      </div>
                    ))}
                  </div>
                  <span className="text-[10.5px] text-text-muted">{fmtInt(g.n)} queries no goldset</span>
                </div>
              ))
            )}
            <p className="text-[11px] text-text-muted">Goldsets de Rebeca e Flora existem, mas ainda sem métricas computadas.</p>
          </div>
        </div>
      </div>
    </Shell>
  );
}
